import { useState } from 'react'
import { Search, Globe, Shield, RefreshCw, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface RepResult {
  ip: string
  verdict: string
  tone: 'rose' | 'amber' | 'emerald' | 'blue'
  greynoise: any
  abuseipdb: any
}

const VERDICT_LABEL: Record<string, string> = {
  block: 'BLOCK',
  trusted: 'TRUSTED',
  'scanner-noise': 'SCANNER NOISE',
  watch: 'WATCH',
  unknown: 'UNKNOWN',
}

const TONE: Record<string, string> = {
  rose: 'bg-rose/15 ring-rose/40 text-rose',
  emerald: 'bg-emerald/15 ring-emerald/40 text-emerald',
  amber: 'bg-amber/15 ring-amber/40 text-amber-light',
  blue: 'bg-blue/15 ring-blue/40 text-blue-light',
}

export default function IpReputationPage() {
  const [ip, setIp] = useState('')
  const [data, setData] = useState<RepResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const lookup = async () => {
    if (!ip.trim()) return
    setLoading(true); setError(null); setData(null)
    try {
      const r = await api.get('/api/sec/ip-reputation', { params: { ip: ip.trim() } })
      setData(r.data)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Lookup failed')
    } finally { setLoading(false) }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <Globe size={24} className="text-cyan" /> IP Reputation
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          GreyNoise Community + AbuseIPDB combined verdict. Block / trust / ignore in one glance.
        </p>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-2">
          <label className="space-y-1 flex-1 min-w-[260px]">
            <span className="text-[10px] text-text-muted uppercase tracking-wider">IPv4 address</span>
            <input value={ip} onChange={e => setIp(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && lookup()}
              placeholder="e.g. 8.8.8.8" className="input w-full py-1.5 text-xs font-mono" />
          </label>
          <button type="button" onClick={lookup} disabled={loading || !ip.trim()}
            className="btn-primary text-xs">
            {loading ? <RefreshCw size={12} className="animate-spin" /> : <Search size={12} />}
            Look up
          </button>
        </div>
        <p className="text-[10px] text-text-muted mt-2">
          GreyNoise Community is free + no auth · AbuseIPDB requires <code className="font-mono">ABUSEIPDB_API_KEY</code> env var (free 1k/day).
        </p>
      </div>

      {error && <div className="card p-3 bg-rose/10 border border-rose/30 text-xs text-rose-light">{error}</div>}

      {data && (
        <div className="space-y-3">
          {/* Verdict hero */}
          <div className={clsx('rounded-xl p-5 ring-2', TONE[data.tone])}>
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Verdict</div>
            <div className="text-3xl font-black mt-1 font-mono">{VERDICT_LABEL[data.verdict] || data.verdict.toUpperCase()}</div>
            <div className="text-[11px] text-text-secondary mt-2 font-mono">{data.ip}</div>
          </div>

          {/* GreyNoise */}
          <div className="card p-4">
            <h3 className="text-xs font-semibold text-text-primary mb-2 flex items-center gap-2">
              <Globe size={12} className="text-cyan" /> GreyNoise Community
              {data.greynoise?.link && (
                <a href={data.greynoise.link} target="_blank" rel="noreferrer" className="ml-auto text-[10px] text-blue-light inline-flex items-center gap-1">
                  view <ExternalLink size={9} />
                </a>
              )}
            </h3>
            {!data.greynoise?.available ? (
              <p className="text-[11px] text-text-muted italic">Unavailable: {data.greynoise?.error}</p>
            ) : !data.greynoise.seen ? (
              <p className="text-[11px] text-text-muted italic">Not seen by GreyNoise — too quiet to be tracked.</p>
            ) : (
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                <Field label="Classification" value={data.greynoise.classification} mono />
                <Field label="Name"           value={data.greynoise.name} />
                <Field label="Last seen"      value={data.greynoise.last_seen} />
                <Field label="Flags"          value={[
                  data.greynoise.noise && 'noise',
                  data.greynoise.riot  && 'RIOT',
                ].filter(Boolean).join(', ') || '—'} />
              </dl>
            )}
          </div>

          {/* AbuseIPDB */}
          <div className="card p-4">
            <h3 className="text-xs font-semibold text-text-primary mb-2 flex items-center gap-2">
              <Shield size={12} className="text-rose" /> AbuseIPDB
            </h3>
            {!data.abuseipdb?.available ? (
              <p className="text-[11px] text-text-muted italic">Unavailable: {data.abuseipdb?.error}</p>
            ) : (
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                <Field label="Abuse confidence" value={`${data.abuseipdb.abuse_confidence}/100`}
                  tone={data.abuseipdb.abuse_confidence >= 75 ? 'rose' : data.abuseipdb.abuse_confidence >= 25 ? 'amber' : 'emerald'} />
                <Field label="Country"  value={data.abuseipdb.country} />
                <Field label="ISP"      value={data.abuseipdb.isp} />
                <Field label="Domain"   value={data.abuseipdb.domain} />
                <Field label="Usage"    value={data.abuseipdb.usage_type} />
                <Field label="Reports"  value={data.abuseipdb.total_reports} />
                <Field label="Last reported" value={data.abuseipdb.last_reported?.slice(0, 10)} />
                <Field label="Tor"      value={data.abuseipdb.is_tor ? 'YES' : 'no'} tone={data.abuseipdb.is_tor ? 'rose' : undefined} />
              </dl>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function Field({ label, value, mono, tone }: { label: string; value: any; mono?: boolean; tone?: 'rose' | 'amber' | 'emerald' }) {
  const v = value == null || value === '' ? '—' : String(value)
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald' : 'text-text-primary'
  return (
    <div>
      <dt className="text-[9px] uppercase tracking-wider text-text-muted">{label}</dt>
      <dd className={clsx('mt-0.5 truncate', mono && 'font-mono', t)}>{v}</dd>
    </div>
  )
}
